import { useState, useEffect, useMemo } from 'react';
import { useConfig } from '../context/ConfigContext';
import { PosSessionProvider } from '../context/PosSessionContext';
import { verifyCredentials } from '../utils/authServiceUtil';
import {
  startTillSession, getTillSession, touchTillSession, clearTillSession,
  tillSessionIdleMs, ADMIN_IDLE_TIMEOUT_MS,
} from '../utils/posSessionUtil';
import {
  POS_TABS, POS_TAB_PERMISSIONS, POS_SUBSCRIPTION_STATUS, POS_EXPIRED_GRACE_MS, POS_AUDIT_ACTIONS,
} from '../constants/posConst';
import { LOGIN_TILL_SUBTITLE, LOGIN_FORGOT_TILL } from '../constants/posLoginConst';
import { isStaffOfClient, fetchStaffPermissions } from '../utils/posClientUtil';
import { logPosEvent } from '../utils/posUtil';
import PosClientNotFound from './PosClientNotFound';
import { loadingSpinner } from '../utils/bookingUtils';
import PosLoginScreen from './PosLoginScreen';
import PosLoginForm from './PosLoginForm';
import PosLoginSupportLine from './PosLoginSupportLine';
import PosTopBar from './PosTopBar';
import PosSidebar from './PosSidebar';
import PosInventory from './PosInventory';
import PosDashboard from './PosDashboard';
import PosSales from './PosSales';
import PosAuditTrail from './PosAuditTrail';
import PosConfigScreen from './PosConfigScreen';
import PosSubscriptionBanner from './PosSubscriptionBanner';
import PosSubscriptionScreen from './PosSubscriptionScreen';
import PosClientDisabledScreen from './PosClientDisabledScreen';
import PosExpiredLock from './PosExpiredLock';
import { useClientAccess } from '../hooks/useClientAccess';
import { useClientSubscription } from '../hooks/useClientSubscription';
import { getSubscriptionState } from '../utils/subscriptionDateUtil';
import '../css/Pos.css';

/**
 * Point of Sale — one client's counter app, at /<client-id>.
 *
 * MULTI-TENANT. The client comes from the URL and is resolved by
 * ConfigProvider, which is mounted per route; every screen below reads it from
 * useConfig() and every database call is scoped to it by posUtil. Nothing here
 * can reach another client's data, because nothing here builds a path.
 *
 * TWO GATES, in this order: the staff list on the client record says whether
 * this address belongs at THIS till, and only then does the password say
 * whether the person typing it is that address. Membership first means an
 * account from another client never authenticates here at all.
 *
 * Only a SUPERUSER (system@gmail.com) skips the staff list. An ordinary
 * administrator — admin@gmail.com included — has to be on a client's staff
 * list like anyone else to work that client's counter.
 *
 * ITS OWN SESSION. The password is checked on a throwaway auth instance
 * (verifyCredentials) and the verified address is held in this component's
 * state — never in the primary Firebase session, which is persisted and shared
 * by every tab on this origin. Putting it there is what used to evict the
 * administrator, and any other client's till, from the same browser.
 *
 * So /admin, /club-a and /club-b can each be signed in as someone
 * different at the same time, and signing out of one touches none of the
 * others. The till's database access rides on the app's shared account, which
 * is all the rules ask for: WHICH client a person may serve is decided by that
 * client's staff list, not by the token.
 *
 * REFRESHING DOES NOT SIGN ANYONE OUT. The verified address is remembered per
 * client (posSessionUtil) — in localStorage when "Keep me signed in" is
 * ticked, in sessionStorage (ends with the tab) when it is not. Either way it
 * is governed by Admin -> Config's idle setting: ticked, a till idle for 30
 * minutes signs itself out — both while the page is open and across a
 * reload; unticked, the session lasts until someone signs out (or, without
 * "Keep me signed in", until the tab closes).
 *
 * Restoring is not blind trust: the record holds an address, not a password,
 * so the client's staff list is re-checked before it is honoured. An address
 * removed from that list cannot come back through a stale record.
 *
 * ROLES DECIDE WHAT IS ON SCREEN. The client assigns each of its staff a role
 * (Admin -> Client Registration -> Staff), and the permissions that role
 * carries are resolved once at sign-in and held in PosSessionContext. A screen
 * whose permission is missing is not offered at all — it is absent from the
 * menu rather than present and refusing, because an entry that only ever says
 * no is a worse answer than one that is not there.
 *
 * An account with NO role keeps full access; see
 * POS_UNASSIGNED_STAFF_UNRESTRICTED for why that, and not the reverse, is the
 * default. This is UI enforcement: the database rules still admit any
 * signed-in account, so a role shapes the job, it does not seal the data.
 *
 * @returns {JSX.Element}
 */
function PosTill() {
  const { config, clientId, clientMissing, loading: configLoading } = useConfig();
  // Whether an administrator has shut this till (an expired, unrenewed plan).
  const { disabled: tillDisabled, loading: accessLoading } = useClientAccess(clientId);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [signedInEmail, setSignedInEmail] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState(POS_TABS[0].key);
  // What the signed-in account may do here, resolved once per sign-in. null
  // until then, which is why nothing below renders on a half-known session.
  const [access, setAccess] = useState(null);
  // Distinct from "not signed in": until the stored session has been read and
  // its membership re-checked, showing the sign-in card would flash a login
  // screen at staff who are still signed in.
  const [restoring, setRestoring] = useState(true);
  // The menu drawer. Closed on every render of a fresh till, and closed by the
  // drawer itself as soon as a screen is chosen.
  const [menuOpen, setMenuOpen] = useState(false);

  const idleTimeoutEnabled = config?.adminIdleTimeoutEnabled !== false;

  // Restores this client's session, if it has one that is still inside its
  // idle window. Re-runs when the client changes, so moving to another
  // client's URL picks up THAT client's session — or none — rather than
  // carrying this one across.
  useEffect(() => {
    let active = true;
    setIsAuthenticated(false);
    setSignedInEmail('');
    setAccess(null);
    setLoginError('');
    setRestoring(true);

    if (!clientId || configLoading) return undefined;

    const session = getTillSession(clientId, idleTimeoutEnabled);
    if (!session?.email) {
      setRestoring(false);
      return undefined;
    }

    // The stored record proves someone signed in here once, not that they may
    // still do so. Membership is the authority, and it can have been revoked
    // since — as can the role, which is why this resolves both in one read
    // rather than restoring first and asking about permissions afterwards.
    fetchStaffPermissions(clientId, session.email)
      .then((resolved) => {
        if (!active) return;
        if (resolved.isStaff) {
          setSignedInEmail(session.email);
          setAccess(resolved);
          setIsAuthenticated(true);
          touchTillSession(clientId);
        } else {
          clearTillSession(clientId);
          setLoginError('That account is no longer registered for this client. Please sign in again.');
        }
      })
      .catch((error) => {
        // A failed check is not a pass. The session stays in storage so a
        // passing network blip does not cost a shift its sign-in; it simply
        // has to be restored on the next try.
        console.error('Could not restore the till session:', error);
      })
      .finally(() => { if (active) setRestoring(false); });

    return () => { active = false; };
  }, [clientId, idleTimeoutEnabled, configLoading]);

  // Keeps the idle window open while the till is in use, and closes it when it
  // is not. Both halves are needed: without the first, a busy counter would be
  // signed out mid-shift; without the second, "sign idle tills out" would only
  // ever take effect on a reload.
  useEffect(() => {
    if (!isAuthenticated || !clientId) return undefined;

    const bump = () => touchTillSession(clientId);
    window.addEventListener('click', bump);
    window.addEventListener('keydown', bump);

    let timer;
    if (idleTimeoutEnabled) {
      // Half a minute is fine-grained enough for a 30-minute window and costs
      // one localStorage read each time.
      timer = setInterval(() => {
        const idleFor = tillSessionIdleMs(clientId);
        if (idleFor === null || idleFor > ADMIN_IDLE_TIMEOUT_MS) {
          logPosEvent({
            clientId,
            action: POS_AUDIT_ACTIONS.STAFF_SIGNED_OUT,
            entityId: null,
            summary: { reason: 'Idle for 30 minutes' },
            actor: signedInEmail,
          });
          clearTillSession(clientId);
          setIsAuthenticated(false);
          setSignedInEmail('');
          setLoginError('This till was signed out after 30 minutes without use.');
        }
      }, 30000);
    }

    return () => {
      window.removeEventListener('click', bump);
      window.removeEventListener('keydown', bump);
      if (timer) clearInterval(timer);
    };
  }, [isAuthenticated, clientId, idleTimeoutEnabled, signedInEmail]);

  // The screens this account may open. A screen it may not is ABSENT rather
  // than present-and-refusing: a tab that only ever says no is a worse answer
  // than a tab that was never there.
  const visibleTabs = useMemo(() => {
    if (!access) return [];
    return POS_TABS.filter(({ key }) => {
      const needed = POS_TAB_PERMISSIONS[key];
      if (!needed) return true;
      return access.unrestricted || access.permissions.includes(needed);
    });
  }, [access]);

  // DERIVED rather than corrected in an effect. Correcting `tab` afterwards
  // would render one frame of a screen this account cannot open — brief, but
  // it is the frame that shows the sales figures.
  const activeTab = visibleTabs.some(({ key }) => key === tab)
    ? tab
    : (visibleTabs[0]?.key || '');

  /**
   * Signs a member of staff in at this till. Called by PosLoginForm once both
   * fields pass its inline checks; a refusal is reported through loginError.
   *
   * @param {string} email
   * @param {string} password
   * @param {boolean} remember - "Keep me signed in": keep the session after the tab closes
   * @returns {Promise<void>}
   */
  const handleLogin = async (email, password, remember) => {
    setLoginError('');
    try {
      // STEP 1 — is this address allowed at this till at all?
      //
      // Before the password, so an account belonging to another client never
      // authenticates here: no session to tidy up, and no window in which the
      // app is signed in as someone this client was never given.
      const allowed = await isStaffOfClient(clientId, email);
      if (!allowed) {
        setLoginError(`That account is not registered for ${config?.courtName || 'this client'}. Ask an administrator to add it.`);
        return;
      }

      // STEP 2 — and is the person typing it actually that account?
      //
      // Verified on a throwaway auth instance, so this does NOT sign the
      // browser in as them: the administrator's session in another tab, and
      // any other client's till, are left exactly as they were.
      const authenticatedEmail = await verifyCredentials(email, password);

      // Firebase looks accounts up by address, so the confirmed address is the
      // typed one bar its casing — but this is an access decision, so it is
      // re-checked against what Firebase confirmed rather than assumed.
      if (authenticatedEmail.trim().toLowerCase() !== String(email).trim().toLowerCase()
        && !(await isStaffOfClient(clientId, authenticatedEmail))) {
        setLoginError(`That account is not registered for ${config?.courtName || 'this client'}. Ask an administrator to add it.`);
        return;
      }

      // STEP 3 — what may they do once they are in?
      //
      // Resolved before the till renders rather than after, so nobody sees a
      // screen appear and then vanish as their role arrives.
      setAccess(await fetchStaffPermissions(clientId, authenticatedEmail));

      setSignedInEmail(authenticatedEmail);
      setIsAuthenticated(true);
      startTillSession(clientId, authenticatedEmail, remember);
      // A restored session is not logged — nobody signed in, the page just
      // reloaded. Only a password actually checked here counts.
      logPosEvent({
        clientId,
        action: POS_AUDIT_ACTIONS.STAFF_SIGNED_IN,
        entityId: null,
        summary: { keptSignedIn: remember ? 'Yes' : 'No — ends when the tab closes' },
        actor: authenticatedEmail,
      });
    } catch (error) {
      console.error('POS sign-in failed:', error.code);
      // Same three buckets AdminPage uses — a wrong password and an unknown
      // account deliberately read alike, so the form cannot be used to find
      // out which staff emails exist.
      if (['auth/invalid-credential', 'auth/user-not-found', 'auth/wrong-password'].includes(error.code)) {
        setLoginError('Invalid email or password.');
      } else if (error.code === 'auth/invalid-email') {
        setLoginError('Please enter a valid email format.');
      } else {
        setLoginError('Login failed. Please try again.');
      }
    }
  };

  // Nothing global to undo: this till never took the browser's Firebase
  // session, so ending a shift is dropping this client's own record. The
  // shared account the app signed in with at startup stays exactly as it was,
  // and every other tab — the admin screen, another client's till — is
  // untouched.
  const handleLogout = () => {
    logPosEvent({
      clientId,
      action: POS_AUDIT_ACTIONS.STAFF_SIGNED_OUT,
      entityId: null,
      summary: { reason: 'Signed out' },
      actor: signedInEmail,
    });
    clearTillSession(clientId);
    setIsAuthenticated(false);
    setSignedInEmail('');
    setAccess(null);
    setLoginError('');
  };

  // The sign-in card carries the club's name and logo, so it waits for config
  // rather than rendering a placeholder name and correcting itself a moment
  // later in front of whoever is signing in.
  if (configLoading) {
    return loadingSpinner('', config?.logoUrl);
  }

  // A client id that is not registered. Distinct from a loading state and from
  // an empty till: an unbranded but working counter would be the worst of the
  // three answers to a mistyped address.
  if (clientMissing) {
    return <PosClientNotFound clientId={clientId} />;
  }

  // DISABLED BY AN ADMINISTRATOR — checked before the sign-in card and before
  // any session is restored, so a signed-in till is shut as well, live, the
  // moment the flag is set. Waiting on the check avoids flashing the till at
  // a club that is locked out.
  if (accessLoading) {
    return loadingSpinner('', config?.logoUrl);
  }
  if (tillDisabled) {
    return <PosClientDisabledScreen />;
  }

  // Still deciding whether this browser is already signed in here. Showing the
  // sign-in card first and replacing it a moment later would look like being
  // signed out and back in.
  if (restoring) {
    return loadingSpinner('', config?.logoUrl);
  }

  if (!isAuthenticated) {
    return (
      <PosLoginScreen
        title={config?.courtName || 'Point of Sale'}
        subtitle={LOGIN_TILL_SUBTITLE}
        help={<PosLoginSupportLine />}
      >
        <PosLoginForm
          onSubmit={handleLogin}
          error={loginError}
          forgotMessage={LOGIN_FORGOT_TILL}
        />
      </PosLoginScreen>
    );
  }

  // Signed in, and allowed nothing. Distinct from being refused at sign-in:
  // the credentials were right and the account IS staff here, so the answer
  // is about the role, and it names the role so whoever is standing there can
  // repeat it to the person who can change it.
  if (visibleTabs.length === 0) {
    return (
      <PosLoginScreen
        title={config?.courtName || 'Point of Sale'}
        subtitle={`Signed in as ${signedInEmail}`}
        help={<PosLoginSupportLine />}
      >
        <div className="pl-notice pl-notice-error" role="alert">
          {access?.roleName
            ? `The "${access.roleName}" role does not open any screen at this till.`
            : 'This account has not been given access to any screen at this till.'}
        </div>
        <p className="pl-panel-msg">Ask an administrator to change the role on your account.</p>
        <button type="button" className="pl-submit" onClick={handleLogout}>Sign out</button>
      </PosLoginScreen>
    );
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'inventory': return <PosInventory />;
      case 'sales': return <PosSales />;
      case 'audit': return <PosAuditTrail />;
      case 'subscriptions': return <PosSubscriptionScreen />;
      case 'config': return <PosConfigScreen />;
      case 'dashboard':
      default: return <PosDashboard />;
    }
  };

  return (
    // Everything below is served BY the address this till verified, so the
    // screens can record who rang a sale up without asking Firebase — which
    // would answer with the app's shared account. The same session carries
    // the role's permissions, which is what the screens check before offering
    // an action.
    <PosSessionProvider
      staffEmail={signedInEmail}
      permissions={access?.permissions || []}
      unrestricted={Boolean(access?.unrestricted)}
      isStoreAdmin={Boolean(access?.isStoreAdmin)}
      roleName={access?.roleName || ''}
    >
      <div className="pos-shell">
        {/* The court-booking system's shape: the club on the left of the bar,
            one Menu button on the right, and the screens in the drawer it
            opens. The till's screens are wide — a product grid, a sales table
            — so the menu is somewhere you go rather than a column they give up
            a quarter of the tablet to all day. */}
        <PosTopBar
          title={config?.courtName || 'Point of Sale'}
          logoUrl={config?.logoUrl}
          onOpenMenu={() => setMenuOpen(true)}
        />

        {/* Directly under the header and full width, OUTSIDE pos-main, so it
            reads as a notice about the whole till rather than part of one
            screen — and shows whichever screen is open. Hidden while all is
            well. */}
        <PosSubscriptionBanner />

        <main className="pos-main">{renderTab()}</main>

        <PosSidebar
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          brandName={config?.courtName || 'Point of Sale'}
          logoUrl={config?.logoUrl}
          tabs={visibleTabs}
          activeTab={activeTab}
          onTabChange={setTab}
          signedInEmail={signedInEmail}
          onSignOut={handleLogout}
          navLabel="POS screens"
        />
      </div>
    </PosSessionProvider>
  );
}

/** How often the page re-checks whether the plan has just run out. */
const EXPIRY_RECHECK_MS = 60 * 1000;

/**
 * The till, plus the lock that covers it once the plan or free trial has
 * EXPIRED.
 *
 * FIVE SECONDS OF NORMAL USE FIRST. The till opens and works as usual; only
 * after POS_EXPIRED_GRACE_MS does the "expired" popup appear, over whichever
 * screen the club has moved to by then.
 *
 * The lock sits OUTSIDE the till rather than replacing it, so whatever was on
 * screen — the sign-in card, a half-rung sale — stays visible (blurred and
 * frozen) behind the popup, and nothing is lost if the plan is renewed.
 *
 * Re-checks every minute, so a till left open over midnight on its last day
 * locks without anyone refreshing. A client an administrator has DISABLED
 * gets PosClientDisabledScreen from the till itself instead, so the two never
 * stack.
 *
 * @returns {JSX.Element}
 */
function PosPage() {
  const { client, clientId, clientMissing, loading } = useConfig();
  const { subscription } = useClientSubscription(clientId);
  const { disabled } = useClientAccess(clientId);
  const [, setTick] = useState(0);
  const [graceOver, setGraceOver] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), EXPIRY_RECHECK_MS);
    return () => clearInterval(timer);
  }, []);

  const expired = !loading && !clientMissing && !disabled && Boolean(subscription)
    && getSubscriptionState(subscription, client?.timezone).status === POS_SUBSCRIPTION_STATUS.EXPIRED;

  // The grace period starts the moment the plan is known to be expired —
  // on load, or at midnight on a till left open — and is cancelled if that
  // stops being true (a renewal approved, the plan extended).
  useEffect(() => {
    setGraceOver(false);
    if (!expired) return undefined;
    const timer = setTimeout(() => setGraceOver(true), POS_EXPIRED_GRACE_MS);
    return () => clearTimeout(timer);
  }, [expired]);

  return (
    <>
      <PosTill />
      {expired && graceOver && <PosExpiredLock />}
    </>
  );
}

export default PosPage;
